import {useCallback, useInsertionEffect} from 'react';

import useUI from 'coreHooks/useUI';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {SSR_STYLE_ELEMENT_ID} from 'brand/app/constants';

import {IUseSSRStyleResult, TSsrStyleRegistrar, TSsrStyleRemover} from './IUseSsrStyle';

/**
 * Registers dynamic SSR styles in Redux (`ui.ssrGlobalStyles`).
 *
 * Returns `addStyle` / `deleteStyle` for producers (grid, carousel, page…).
 * Call `useSyncSSRStyle()` once at the app root to keep `<style id="SSR_style">` in sync on the client.
 * Delegates to UiMgr so all UI mutations share a single write path; usages counted during SSR
 * are claimed by the hydrating client mounts (see `receiveSsrGlobalStyle`), so add/delete stay
 * balanced regardless of when each Suspense boundary hydrates.
 *
 * On the server the style tag is injected from Redux state (react-rendering.js; Vite brands: `react-router/ssrStyles`).
 */
const useSSRStyle = (): IUseSSRStyleResult => {
	const {managerRepository} = useGeneralProvider();

	const addStyle = useCallback<TSsrStyleRegistrar>((key, value) => {
		managerRepository.getUiMgr().addSsrGlobalStyle(key, value);
	}, []);

	const deleteStyle = useCallback<TSsrStyleRemover>((key) => {
		managerRepository.getUiMgr().removeSsrGlobalStyle(key);
	}, []);

	return {addStyle, deleteStyle};
};

/** `<style>` written by the Vite server before each section streamed after the shell (`react-router/ssrStyles`). */
const LATE_SSR_STYLE_SELECTOR = 'style[data-ssr-style]';

const useSyncSSRStyle = () => {
	const ssrGlobalStyles = useUI('ssrGlobalStyles');

	useInsertionEffect(() => {
		const el = document.getElementById(SSR_STYLE_ELEMENT_ID);

		if (!el) {
			return;
		}

		el.textContent = Object.values(ssrGlobalStyles || {})
			.map(({style}) => style)
			.join('\n');
		// Their rules are in the Redux state (frozen at the end of the stream), now in `#SSR_style`.
		document.querySelectorAll(LATE_SSR_STYLE_SELECTOR).forEach((style) => style.remove());
	}, [ssrGlobalStyles]);
};

export default useSSRStyle;
export {useSyncSSRStyle};
